import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  LinearProgress,
  Alert,
  Grid,
  Chip,
  CircularProgress,
  Fab,
  Tooltip,
  useMediaQuery,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
  AlertTitle,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ArrowBack,
  Group,
  AttachMoney,
  Folder,
  Settings,
  TrendingUp,
  Person,
  FolderOpen,
  ChevronLeft,
  MoreVert,
  CheckCircle,
  Cancel,
  Schedule,
  Payments,
  Receipt,
  AccountBalance,
  CreditCard,
  Warning,
  Info,
  Error as ErrorIcon,
} from '@mui/icons-material';

import client from '../../../../API';
import useAuth from '../../../../Hooks/use-auth';
import PageTitle from '../../../../Components/PageTitle';
import { replaceConsoleError } from '../../../../Utils/toastNotifications';
import useAlertDialog from '../../../../Components/useAlertDialog';
import AlertDialog from '../../../../Components/AlertDialog';
import CastingCallChatDrawer from './manageCasting/castingCallChatDrawer';

import { GradientCard, StatCard } from './manageCasting/gradientCard';
import ApplicantCard from './manageCasting/ApplicantCard';
import PayoutDialog from './manageCasting/PayoutDialog';
import BudgetDialog from './manageCasting/BudgetDialog';
import { getApplicantDisplayName } from './manageCasting/castingCallHelpers';
import CollaboratorView from './manageCasting/creatorCastingManage';
import OrganizerSidebar from './manageCasting/OrganizerSidebar';
import SettingsDialog from './manageCasting/SettingsDialog';

// ✅ UPDATED: Enhanced metrics calculation using API budget status
const calculateEnhancedMetrics = (castingCall, payoutsData, budgetStatus) => {
  if (!castingCall) return null;

  const applicants = castingCall.applicants || [];
  
  // Use budgetStatus from API instead of calculating locally
  const budget = budgetStatus ? parseFloat(budgetStatus.budget) : parseFloat(castingCall.budget || 0);
  const allocated = budgetStatus ? parseFloat(budgetStatus.allocated) : 0;
  const pending = budgetStatus ? parseFloat(budgetStatus.pending || 0) : 0;
  const approved = budgetStatus ? parseFloat(budgetStatus.approved || 0) : 0;
  const remaining = budgetStatus ? parseFloat(budgetStatus.remaining) : budget;

  // Get assigned payouts from deliverables_scope
  const payoutAssignments = castingCall.deliverables_scope?.payout_assignments || {};
  const assignedPayouts = Object.values(payoutAssignments).reduce((sum, amt) => sum + parseFloat(amt || 0), 0);

  return {
    totalApplicants: applicants.length,
    budget,
    allocated,
    pending,
    approved,
    assignedPayouts, // Assigned but not yet created as payouts
    remainingBudget: remaining,
    organizerCount: castingCall.organizers?.length || 0,
    utilizationPercent: budget > 0 ? (allocated / budget) * 100 : 0,
  };
};

const ManageCastingCall = () => {
  const { id: castingCallId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [selectedApplicantForChat, setSelectedApplicantForChat] = useState(null);
  const [payoutMenuAnchor, setPayoutMenuAnchor] = useState(null);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Fetch casting call details
  const {
    data: castingCall,
    isLoading,
    error: fetchError,
    refetch,
  } = useQuery(
    ['castingCall', castingCallId],
    () => client.castingCalls.getById(castingCallId),
    { 
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  // ✅ UPDATED: Fetch budget status with refetch reference
  const {
    data: budgetStatus,
    isLoading: loadingBudget,
    refetch: refetchBudgetStatus,
  } = useQuery(
    ['castingCallBudget', castingCallId],
    () => client.castingCalls.getBudgetStatus(castingCallId),
    {
      enabled: !!castingCallId && !!castingCall?.is_owner,
      refetchOnWindowFocus: false,
      select: (data) => data.budget_status,
    }
  );

  // Fetch payouts for this casting call
  const {
    data: payoutsData,
    isLoading: loadingPayouts,
    refetch: refetchPayouts,
  } = useQuery(
    ['castingCallPayouts', castingCallId],
    () => client.castingCalls.getPayouts(castingCallId),
    {
      enabled: !!castingCallId && !!castingCall?.is_owner,
      refetchOnWindowFocus: false,
      select: (data) => data.payouts || [],
    }
  );

  // Reject applicant mutation
  const { mutate: rejectApplicant, isLoading: isRejecting } = useMutation(
    (data) => client.castingCalls.reject(castingCallId, data),
    {
      onSuccess: () => {
        openDialog('Success', 'Applicant rejected successfully', closeDialog, closeDialog, 'Ok');
        queryClient.invalidateQueries(['castingCall', castingCallId]);
      },
      onError: (error) => {
        replaceConsoleError('Error rejecting applicant', error);
        openDialog(
          'Error',
          `Failed to reject applicant: ${error?.response?.data?.error || error.message}`,
          closeDialog,
          closeDialog,
          'Ok'
        );
      },
    }
  );

  // Select applicant mutation
  const { mutate: selectApplicant, isLoading: isSelecting } = useMutation(
    (data) => client.castingCalls.select(castingCallId, data),
    {
      onSuccess: () => {
        openDialog('Success', 'Applicant accepted successfully! Organizers have been shared.', closeDialog, closeDialog, 'Ok');
        queryClient.invalidateQueries(['castingCall', castingCallId]);
      },
      onError: (error) => {
        replaceConsoleError('Error selecting applicant', error);
        openDialog(
          'Error',
          `Failed to accept applicant: ${error?.response?.data?.error || error.message}`,
          closeDialog,
          closeDialog,
          'Ok'
        );
      },
    }
  );

  // Approve payout mutation
  const { mutate: approvePayout, isLoading: isApprovingPayout } = useMutation(
    (payoutId) => client.castingCalls.approvePayout(payoutId),
    {
      onSuccess: () => {
        openDialog('Success', 'Payout approved and funded successfully!', closeDialog, closeDialog, 'Ok');
        refetchPayouts();
        refetch();
        refetchBudgetStatus(); // ✅ ADDED: Refresh budget status
      },
      onError: (error) => {
        replaceConsoleError('Error approving payout', error);
        openDialog(
          'Error',
          `Failed to approve payout: ${error?.response?.data?.error || error.message}`,
          closeDialog,
          closeDialog,
          'Ok'
        );
      },
    }
  );

  // Cancel payout mutation
  const { mutate: cancelPayout, isLoading: isCancellingPayout } = useMutation(
    (payoutId) => client.castingCalls.cancelPayout(payoutId),
    {
      onSuccess: () => {
        openDialog('Success', 'Payout cancelled successfully', closeDialog, closeDialog, 'Ok');
        refetchPayouts();
        refetch();
        refetchBudgetStatus(); // ✅ ADDED: Refresh budget status
      },
      onError: (error) => {
        replaceConsoleError('Error cancelling payout', error);
        openDialog(
          'Error',
          `Failed to cancel payout: ${error?.response?.data?.error || error.message}`,
          closeDialog,
          closeDialog,
          'Ok'
        );
      },
    }
  );

  // ✅ UPDATED: Calculate metrics using enhanced function with budget status
  const metrics = useMemo(() => 
    calculateEnhancedMetrics(castingCall, payoutsData, budgetStatus), 
    [castingCall, payoutsData, budgetStatus]
  );

  // Memoized handlers
  const handleAcceptApplicant = useCallback((applicant) => {
    const displayName = getApplicantDisplayName(applicant, castingCall);
    openDialog(
      'Confirm Selection',
      `Accept ${displayName} for this casting call? This will automatically share all organizers with them.`,
      () => {
        const payload = castingCall.call_type === 'creator'
          ? { creatorUsername: applicant.username }
          : { userId: applicant.id };
        selectApplicant(payload);
        closeDialog();
      },
      closeDialog,
      'Accept',
      'Cancel'
    );
  }, [castingCall, openDialog, closeDialog, selectApplicant]);

  const handleRejectApplicant = useCallback((applicant) => {
    const displayName = getApplicantDisplayName(applicant, castingCall);
    openDialog(
      'Confirm Rejection',
      `Are you sure you want to reject ${displayName}?`,
      () => {
        const payload = castingCall.call_type === 'creator'
          ? { creatorUsername: applicant.username }
          : { userId: applicant.id };
        rejectApplicant(payload);
        closeDialog();
      },
      closeDialog,
      'Reject',
      'Cancel'
    );
  }, [castingCall, openDialog, closeDialog, rejectApplicant]);

  const handleCreatePayout = useCallback((applicant) => {
    setSelectedApplicant(applicant);
    setPayoutDialogOpen(true);
  }, []);

  // ✅ UPDATED: Refresh budget status on payout success
  const handlePayoutSuccess = useCallback(() => {
    refetchPayouts();
    refetch();
    refetchBudgetStatus();
  }, [refetchPayouts, refetch, refetchBudgetStatus]);

  const handleOpenChat = useCallback((applicant) => {
    setSelectedApplicantForChat(applicant);
    setChatDrawerOpen(true);
  }, []);

  const handlePayoutMenuOpen = useCallback((event, payout) => {
    setPayoutMenuAnchor(event.currentTarget);
    setSelectedPayout(payout);
  }, []);

  const handlePayoutMenuClose = useCallback(() => {
    setPayoutMenuAnchor(null);
    setSelectedPayout(null);
  }, []);

  const handleApprovePayout = useCallback(() => {
    if (!selectedPayout) return;
    
    openDialog(
      'Confirm Approval',
      `Approve and fund payout of $${parseFloat(selectedPayout.amount).toFixed(2)} to ${selectedPayout.creator_id || 'recipient'}?`,
      () => {
        approvePayout(selectedPayout.id);
        handlePayoutMenuClose();
        closeDialog();
      },
      closeDialog,
      'Approve',
      'Cancel'
    );
  }, [selectedPayout, approvePayout, handlePayoutMenuClose, openDialog, closeDialog]);

  const handleCancelPayout = useCallback(() => {
    if (!selectedPayout) return;
    
    openDialog(
      'Confirm Cancellation',
      `Cancel payout of $${parseFloat(selectedPayout.amount).toFixed(2)}? This action cannot be undone.`,
      () => {
        cancelPayout(selectedPayout.id);
        handlePayoutMenuClose();
        closeDialog();
      },
      closeDialog,
      'Cancel Payout',
      'Keep'
    );
  }, [selectedPayout, cancelPayout, handlePayoutMenuClose, openDialog, closeDialog]);

  const handleAssignOrganizer = useCallback((applicant) => {
    setSidebarOpen(true);
  }, []);

  const handleIssuePayment = useCallback((applicant) => {
    const displayName = getApplicantDisplayName(applicant, castingCall);
    openDialog(
      'Confirm Payment',
      `Issue payment of $${applicant.assigned_payout?.toFixed(2)} to ${displayName}?`,
      () => {
        // TODO: Implement payment issuance API call
        alert('Payment issuance not yet implemented');
        closeDialog();
      },
      closeDialog,
      'Issue Payment',
      'Cancel'
    );
  }, [castingCall, openDialog, closeDialog]);

  // Get payout status details
  const getPayoutStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <Schedule fontSize="small" />, label: 'Pending' },
      approved: { color: 'info', icon: <CheckCircle fontSize="small" />, label: 'Approved' },
      confirmed: { color: 'success', icon: <CheckCircle fontSize="small" />, label: 'Confirmed' },
      paid: { color: 'success', icon: <Payments fontSize="small" />, label: 'Paid' },
      cancelled: { color: 'error', icon: <Cancel fontSize="small" />, label: 'Cancelled' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Chip
        size="small"
        icon={config.icon}
        label={config.label}
        color={config.color}
        variant="outlined"
      />
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load casting call: {fetchError?.response?.data?.error || fetchError.message}
        </Alert>
        <Button onClick={() => navigate('/collabs')} sx={{ mt: 2 }}>
          Back to Collaborations
        </Button>
      </Container>
    );
  }

  // Not found
  if (!castingCall) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="warning">Casting call not found</Alert>
        <Button onClick={() => navigate('/collabs')} sx={{ mt: 2 }}>
          Back to Collaborations
        </Button>
      </Container>
    );
  }

  // Authorization check
  const isOwner = castingCall.is_owner || castingCall.user_role === 'owner';
  const isCollaborator = castingCall.user_role === 'collaborator';
  
  if (!isOwner && !isCollaborator) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          You don't have permission to manage this casting call.
        </Alert>
        <Button onClick={() => navigate('/collabs')} variant="contained" sx={{ mt: 2 }}>
          Back to Collaborations
        </Button>
      </Container>
    );
  }

  // Collaborator view
  if (isCollaborator && !isOwner) {
    return (
      <>
        <AlertDialog alertState={dialogState} />
        <PageTitle title={`${castingCall.brief?.substring(0, 50)} - Blitz`} />
        
        <CollaboratorView
          castingCall={castingCall}
          onBack={() => navigate('/collabs')}
          onOpenChat={handleOpenChat}
          currentUser={currentUser}
        />

        <CastingCallChatDrawer
          open={chatDrawerOpen}
          onClose={() => setChatDrawerOpen(false)}
          castingCall={castingCall}
          applicant={selectedApplicantForChat}
        />
      </>
    );
  }

  // Owner view
  return (
    <>
      <AlertDialog alertState={dialogState} />
      <PageTitle title={`Manage Casting Call - ${castingCall.brief?.substring(0, 50)} - Blitz`} />

      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {/* Main Content */}
        <Box 
          sx={{ 
            flex: 1, 
            overflow: 'auto',
            mr: sidebarOpen && !isMobile ? '400px' : 0,
            transition: 'margin-right 0.3s',
          }}
        >
          <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/collabs')}
                sx={{ mb: 2 }}
              >
                Back to Collaborations
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    Manage Casting Call
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {castingCall.brief}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={castingCall.call_type === 'creator' ? 'Creator Call' : 'General Work'}
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label={castingCall.status}
                      color={castingCall.status === 'ACTIVE' ? 'success' : 'default'}
                      size="small"
                    />
                    <Chip label="Owner" color="info" size="small" />
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1}>
                  {isMobile && (
                    <Button
                      variant="outlined"
                      startIcon={<FolderOpen />}
                      onClick={() => setSidebarOpen(true)}
                    >
                      Organizers ({metrics?.organizerCount || 0})
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<Settings />}
                    onClick={() => setSettingsDialogOpen(true)}
                  >
                    Settings
                  </Button>
                </Stack>
              </Box>
            </Box>

            {/* Stats Grid */}
            {metrics && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    icon={<Group />}
                    label="Total Applicants"
                    value={metrics.totalApplicants}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    icon={<AttachMoney />}
                    label="Total Budget"
                    value={`$${metrics.budget.toFixed(2)}`}
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    icon={<TrendingUp />}
                    label="Remaining Budget"
                    value={`$${metrics.remainingBudget.toFixed(2)}`}
                    color="info"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    icon={<Folder />}
                    label="Organizers"
                    value={metrics.organizerCount}
                    color="warning"
                  />
                </Grid>
              </Grid>
            )}

            {/* ✅ UPDATED: Enhanced Budget Management Card */}
            {metrics && budgetStatus && (
              <GradientCard sx={{ mb: 4 }}>
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Budget Management
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Track spending and remaining budget
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setBudgetDialogOpen(true)}
                    >
                      Update Budget
                    </Button>
                  </Box>

                  {/* ✅ NEW: Detailed budget breakdown */}
                  <Stack spacing={2} sx={{ mb: 3 }}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Total Budget
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          ${metrics.budget.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Approved Payouts
                        </Typography>
                        <Typography variant="caption" color="success.main" fontWeight={600}>
                          ${metrics.approved.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Pending Payouts
                        </Typography>
                        <Typography variant="caption" color="warning.main" fontWeight={600}>
                          ${metrics.pending.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Total Allocated
                        </Typography>
                        <Typography variant="caption" fontWeight={600}>
                          ${metrics.allocated.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider />

                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          Remaining
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight={600}
                          color={metrics.remainingBudget < 0 ? 'error' : 'primary'}
                        >
                          ${metrics.remainingBudget.toFixed(2)}
                        </Typography>
                      </Box>
                      
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(metrics.utilizationPercent, 100)}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 5,
                            background: metrics.remainingBudget < 0
                              ? `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`
                              : metrics.remainingBudget < metrics.budget * 0.2
                              ? `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`
                              : `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                          },
                        }}
                      />
                      
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {metrics.utilizationPercent.toFixed(1)}% utilized
                      </Typography>
                    </Box>
                  </Stack>

                  {/* ✅ IMPROVED: More informative alerts */}
                  {metrics.remainingBudget < 0 ? (
                    <Alert severity="error" icon={<ErrorIcon />}>
                      <AlertTitle>Budget Exceeded</AlertTitle>
                      Budget has been exceeded by ${Math.abs(metrics.remainingBudget).toFixed(2)}. 
                      Please increase the budget or cancel pending payouts.
                    </Alert>
                  ) : metrics.remainingBudget < metrics.budget * 0.1 ? (
                    <Alert severity="warning" icon={<Warning />}>
                      <AlertTitle>Low Budget</AlertTitle>
                      Only ${metrics.remainingBudget.toFixed(2)} remaining ({(metrics.remainingBudget / metrics.budget * 100).toFixed(1)}% of budget).
                      Consider increasing the budget if more payouts are needed.
                    </Alert>
                  ) : metrics.pending > 0 ? (
                    <Alert severity="info" icon={<Info />}>
                      ${metrics.pending.toFixed(2)} in pending payouts. Approve them to fund creators.
                    </Alert>
                  ) : (
                    <Alert severity="success" icon={<CheckCircle />}>
                      ${metrics.remainingBudget.toFixed(2)} available for new payouts
                    </Alert>
                  )}
                </Box>
              </GradientCard>
            )}

            {/* Applicants Section */}
            <GradientCard sx={{ mb: 4 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Applicants ({castingCall.applicants?.length || 0})
                </Typography>

                {(!castingCall.applicants || castingCall.applicants.length === 0) ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Person sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No applications yet
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {castingCall.applicants.map((applicant, index) => (
                      <ApplicantCard
                        key={applicant.id || applicant.username || index}
                        applicant={applicant}
                        castingCall={castingCall}
                        onAccept={handleAcceptApplicant}
                        onReject={handleRejectApplicant}
                        onAssignPayout={handleCreatePayout}
                        onAssignOrganizer={handleAssignOrganizer}
                        onIssuePayment={handleIssuePayment}
                        onOpenChat={handleOpenChat}
                        payouts={payoutsData}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </GradientCard>

            {/* ✅ UPDATED: Enhanced Payouts Section */}
            {payoutsData && payoutsData.length > 0 && (
              <GradientCard sx={{ mb: 4 }}>
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Payouts
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {payoutsData.length} payout{payoutsData.length !== 1 ? 's' : ''} created
                      </Typography>
                    </Box>
                    <Badge 
                      badgeContent={payoutsData.filter(p => p.status === 'pending').length} 
                      color="warning"
                    >
                      <Receipt />
                    </Badge>
                  </Box>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Recipient</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loadingPayouts ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <CircularProgress size={24} />
                            </TableCell>
                          </TableRow>
                        ) : payoutsData.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No payouts created yet
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          payoutsData.map((payout) => (
                            <TableRow 
                              key={payout.id} 
                              hover
                              sx={{
                                bgcolor: payout.status === 'pending' 
                                  ? alpha(theme.palette.warning.main, 0.05)
                                  : 'inherit'
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {payout.creator_id || payout.user_id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {payout.recipient_type || 'creator'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={600}>
                                  ${parseFloat(payout.amount).toFixed(2)}
                                </Typography>
                                {payout.role === 'Creator' && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    (5% fee)
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {getPayoutStatusChip(payout.status)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={payout.payout_method || 'auto'}
                                  variant="outlined"
                                  icon={
                                    payout.payout_method === 'balance' ? <AccountBalance fontSize="small" /> :
                                    payout.payout_method === 'credit_line' ? <CreditCard fontSize="small" /> :
                                    null
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {new Date(payout.created_at || payout.payout_date).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handlePayoutMenuOpen(e, payout)}
                                  disabled={payout.status === 'paid' || payout.status === 'cancelled'}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </GradientCard>
            )}
          </Container>
        </Box>

        {/* Organizer Sidebar */}
        <OrganizerSidebar
          castingCallId={castingCallId}
          organizers={castingCall.organizers || []}
          applicants={castingCall.applicants || []}
          castingCall={castingCall}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(!sidebarOpen)}
          onRefresh={refetch}
        />

        {/* Toggle Sidebar Button */}
        {!isMobile && !sidebarOpen && (
          <Tooltip title="Open Organizers" placement="left">
            <Fab
              color="primary"
              sx={{
                position: 'fixed',
                right: 16,
                bottom: 16,
              }}
              onClick={() => setSidebarOpen(true)}
            >
              <ChevronLeft />
            </Fab>
          </Tooltip>
        )}
      </Box>

      {/* Payout Menu */}
      <Menu
        anchorEl={payoutMenuAnchor}
        open={Boolean(payoutMenuAnchor)}
        onClose={handlePayoutMenuClose}
      >
        {selectedPayout?.status === 'pending' && (
          <MenuItem onClick={handleApprovePayout}>
            <CheckCircle fontSize="small" sx={{ mr: 1 }} />
            Approve & Fund
          </MenuItem>
        )}
        {selectedPayout?.status === 'pending' && (
          <MenuItem onClick={handleCancelPayout}>
            <Cancel fontSize="small" sx={{ mr: 1 }} />
            Cancel Payout
          </MenuItem>
        )}
        {selectedPayout?.status !== 'pending' && (
          <MenuItem disabled>
            No actions available
          </MenuItem>
        )}
      </Menu>

      {/* Dialogs */}
      <PayoutDialog
        open={payoutDialogOpen}
        onClose={() => setPayoutDialogOpen(false)}
        applicant={selectedApplicant}
        castingCall={castingCall}
        onSuccess={handlePayoutSuccess}
      />

      <BudgetDialog
        open={budgetDialogOpen}
        onClose={() => setBudgetDialogOpen(false)}
        castingCall={castingCall}
        onSuccess={() => {
          refetch();
          refetchBudgetStatus();
          setBudgetDialogOpen(false);
        }}
      />

      <CastingCallChatDrawer
        open={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        castingCall={castingCall}
        applicant={selectedApplicantForChat}
      />

      <SettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        castingCall={castingCall}
        onSuccess={() => {
          refetch();
          refetchBudgetStatus();
          setSettingsDialogOpen(false);
        }}
      />
    </>
  );
};

export default ManageCastingCall;